import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {getCurrentPage, getCurrentPageBackground} from '@Components/poster-editor/poster-editor-reducer';
import type {RGBA} from '@Utils/color.util';
import {getColorObjectArrayFromFillColor} from '@Utils/color.util';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import {getBackgroundDropdownItemForColorFillType, getFillColorForBackgroundDropdownItem} from '@PosterWhiteboard/page/background/color-background.class';
import {ContextualMenuColorFillOption} from '@Components/contextual-menu/components/contextual-menu-color-fill-option';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Thumbnail} from '@Components/thumbnail';
import type {ThumbnailData} from '@Components/thumbnail/thumbnail.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {getAssetUrl} from '@Utils/s3.util';
import {openReplaceBackgroundImageModal} from '@Modals/replace-background-image-modal';
import {ContextualMenuImageThumbnailOption} from '@Components/contextual-menu/components/contextual-menu-image-thumbnail-option';
import {getUrlForImageItemBigTierScreenSize} from '@Libraries/image-item.library';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import {ContextualMenuHorizontalScrollingSection} from '@Components/contextual-menu/components/contextual-menu-horizontal-scrolling-section';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {BackgroundTypeName} from '@/poster-whiteboard/page/background/background.class';
import styles from './background-type-options.module.scss';

enum BACKGROUND_FILL_OPTIONS {
  TRANSPARENT = 'TRANSPARENT',
  SOLID = 'SOLID',
  LINEAR_GRADIENT = 'LINEAR_GRADIENT',
  RADIAL_GRADIENT = 'RADIAL_GRADIENT',
  IMAGE = 'IMAGE',
}

const BACKGROUND_FILL_TYPES = [
  BACKGROUND_FILL_OPTIONS.SOLID,
  BACKGROUND_FILL_OPTIONS.LINEAR_GRADIENT,
  BACKGROUND_FILL_OPTIONS.RADIAL_GRADIENT,
  BACKGROUND_FILL_OPTIONS.IMAGE,
  BACKGROUND_FILL_OPTIONS.TRANSPARENT,
];

interface BackgroundTypeOptionsProps {
  panelId: string;

  onOpenColorPanel(colorIndex: number): void;

  onOpenBackgroundImageOptions(): void;
}

export function BackgroundTypeOptions({...props}: BackgroundTypeOptionsProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const currentPageObject = useAppSelector(getCurrentPage);
  const currentBackgroundObject = useAppSelector(getCurrentPageBackground);
  if (!currentBackgroundObject || !currentPageObject) {
    return null;
  }
  const currentPage = window.posterEditor?.whiteboard?.pages.getPage(currentPageObject.hashedID);

  useEffect(() => {
    updateHeightInRedux(getColorFillType() ?? FillTypes.SOLID);
  }, [currentBackgroundObject.details.type]);

  const updateHeightInRedux = (type: FillTypes): void => {
    dispatch(
      updatePanelSizeData({
        sizeDataId: props.panelId,
        sizeData: {
          height: currentBackgroundObject.details.type === BackgroundTypeName.IMAGE ? '216px' : getHeightAccordingToMorphType(type),
        },
      })
    );
  };

  const getHeightAccordingToMorphType = (type: FillTypes): string => {
    switch (type) {
      case FillTypes.LINEAR_GRADIENT:
      case FillTypes.RADIAL_GRADIENT:
        return '256px';
      case FillTypes.NONE:
        return '158px';
      case FillTypes.SOLID:
      default:
        return '208px';
    }
  };

  const onChangeFillType = (newType: FillTypes): void => {
    void currentPage?.background.updateFromObject({
      details: {
        type: BackgroundTypeName.COLOR,
        fill: {
          fillType: newType,
          fillColor: getColorFill(newType),
        },
      },
    });
    updateHeightInRedux(newType);
  };

  const getColorFillType = (): FillTypes | null => {
    if (!('fill' in currentBackgroundObject.details)) {
      return null;
    }

    return currentBackgroundObject.details.fill.fillType;
  };

  const getBackgroundType = (): BACKGROUND_FILL_OPTIONS => {
    if (currentBackgroundObject.details.type === BackgroundTypeName.IMAGE) {
      return BACKGROUND_FILL_OPTIONS.IMAGE;
    }
    switch (currentBackgroundObject.details.fill.fillType) {
      case FillTypes.SOLID:
        return BACKGROUND_FILL_OPTIONS.SOLID;
      case FillTypes.LINEAR_GRADIENT:
        return BACKGROUND_FILL_OPTIONS.LINEAR_GRADIENT;
      case FillTypes.RADIAL_GRADIENT:
        return BACKGROUND_FILL_OPTIONS.RADIAL_GRADIENT;
      case FillTypes.NONE:
      default:
        return BACKGROUND_FILL_OPTIONS.TRANSPARENT;
    }
  };

  const getColorFill = (type: FillTypes): RGBA[] => {
    return getFillColorForBackgroundDropdownItem(getBackgroundDropdownItemForColorFillType(type));
  };

  const getColorLabel = (index: number): string => {
    if (getColorFillType() === FillTypes.SOLID) {
      return window.i18next.t('pmwjs_color');
    }

    return window.i18next.t(index === 0 ? 'pmwjs_color_one' : 'pmwjs_color_two');
  };

  const getColorFillOptions = (): ReactElement[] => {
    const bgColorType = getColorFillType();
    if (bgColorType === null || bgColorType === FillTypes.NONE) {
      return [];
    }
    return getColorFill(bgColorType).map((color, index) => {
      const optionKey = `text-fill-color-${index}`;
      return (
        <ContextualMenuColorFillOption
          id={`text-fill-color-${index}`}
          key={optionKey}
          label={getColorLabel(index)}
          className="spacing-p-l-4 spacing-p-r-4 spacing-m-b-4"
          data={[
            {
              id: `color-fill-object-${index}`,
              color: getColorObjectArrayFromFillColor([color]),
            },
          ]}
          onClick={(): void => {
            props.onOpenColorPanel(index);
          }}
        />
      );
    });
  };

  const getImgSrcForBackground = (): string => {
    if (currentBackgroundObject.details.type === BackgroundTypeName.IMAGE) {
      if (currentBackgroundObject.details.imageBackgroundItemObject.hashedFilename && currentBackgroundObject.details.imageBackgroundItemObject.imageSource) {
        return getUrlForImageItemBigTierScreenSize(
          currentBackgroundObject.details.imageBackgroundItemObject.hashedFilename,
          currentBackgroundObject.details.imageBackgroundItemObject.imageSource
        );
      }
      if (currentBackgroundObject.details.imageBackgroundItemObject.uploadingImageData?.dataUrl) {
        return currentBackgroundObject.details.imageBackgroundItemObject.uploadingImageData.dataUrl;
      }
    }
    throw new Error('Unable to get image source for image background since background type is not IMAGE');
  };

  const getImageOptions = (): ReactElement | null => {
    if (currentBackgroundObject.details.type === BackgroundTypeName.IMAGE) {
      return (
        <ContextualMenuImageThumbnailOption
          id="image-background-thumbnail-option"
          className="spacing-p-l-4 spacing-p-r-4 spacing-m-b-4"
          label={window.i18next.t('pmwjs_photo')}
          imgSrc={getImgSrcForBackground()}
          onEdit={(): void => {
            props.onOpenBackgroundImageOptions();
          }}
        />
      );
    }
    return null;
  };

  const getFillTypeFromBackgroundType = (type: BACKGROUND_FILL_OPTIONS): FillTypes => {
    switch (type) {
      case BACKGROUND_FILL_OPTIONS.SOLID:
        return FillTypes.SOLID;
      case BACKGROUND_FILL_OPTIONS.LINEAR_GRADIENT:
        return FillTypes.LINEAR_GRADIENT;
      case BACKGROUND_FILL_OPTIONS.RADIAL_GRADIENT:
        return FillTypes.RADIAL_GRADIENT;
      case BACKGROUND_FILL_OPTIONS.TRANSPARENT:
        return FillTypes.NONE;
      case BACKGROUND_FILL_OPTIONS.IMAGE:
      default:
        throw new Error('Invalid background type in background color panel');
    }
  };

  const getTextFromType = (type: BACKGROUND_FILL_OPTIONS): string => {
    switch (type) {
      case BACKGROUND_FILL_OPTIONS.IMAGE:
        return window.i18next.t('pmwjs_image');
      case BACKGROUND_FILL_OPTIONS.RADIAL_GRADIENT:
        return window.i18next.t('pmwjs_gradient_radial');
      case BACKGROUND_FILL_OPTIONS.LINEAR_GRADIENT:
        return window.i18next.t('pmwjs_gradient_linear');
      case BACKGROUND_FILL_OPTIONS.TRANSPARENT:
        return window.i18next.t('pmwjs_transparent');
      case BACKGROUND_FILL_OPTIONS.SOLID:
      default:
        return window.i18next.t('pmwjs_background_solid');
    }
  };

  const getThumbnailData = (option: BACKGROUND_FILL_OPTIONS): ThumbnailData => {
    switch (option) {
      case BACKGROUND_FILL_OPTIONS.IMAGE:
        return {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`poster-editor-thumbnails/none.${getCompatibleImageFileExtension('webp')}`),
        };
      case BACKGROUND_FILL_OPTIONS.SOLID:
        return {
          type: ThumbnailType.CUSTOM,
          content: <div className={`spacing-p-5 radius-4 ${styles.solid}`} />,
        };
      case BACKGROUND_FILL_OPTIONS.LINEAR_GRADIENT:
        return {
          type: ThumbnailType.CUSTOM,
          content: <div className={`spacing-p-5 radius-4 ${styles.linear}`} />,
        };
      case BACKGROUND_FILL_OPTIONS.RADIAL_GRADIENT:
        return {
          type: ThumbnailType.CUSTOM,
          content: <div className={`spacing-p-5 radius-4 ${styles.radial}`} />,
        };
      case BACKGROUND_FILL_OPTIONS.TRANSPARENT:
        return {
          type: ThumbnailType.IMAGE,
          imageSrc: getAssetUrl(`poster-editor-thumbnails/transparent.${getCompatibleImageFileExtension('webp')}`),
        };
      default:
        throw new Error(`Invalid background type ${option}`);
    }
  };

  const getBackgroundTypeOptions = (): (ReactElement | null)[] => {
    return BACKGROUND_FILL_TYPES.map((option) => {
      if (option === BACKGROUND_FILL_OPTIONS.TRANSPARENT && currentPage?.isVideo()) {
        return null;
      }
      return (
        <ContextualMenuThumbnailItem
          key={option}
          thumbnail={<Thumbnail className="spacing-p-2" data={getThumbnailData(option)} />}
          onClick={(): void => {
            if (option === BACKGROUND_FILL_OPTIONS.IMAGE) {
              openReplaceBackgroundImageModal();
            } else {
              onChangeFillType(getFillTypeFromBackgroundType(option));
            }
          }}
          selectionState={ThumbnailItemSelectionState.BORDER}
          isSelected={getBackgroundType() === option}
          text={getTextFromType(option)}
        />
      );
    });
  };

  return (
    <>
      {getColorFillOptions()}
      {getImageOptions()}
      <ContextualMenuHorizontalScrollingSection className={`${styles.optionsContainer} spacing-p-l-4 spacing-p-r-4 flex-h-row spacing-m-0`}>
        {getBackgroundTypeOptions()}
      </ContextualMenuHorizontalScrollingSection>
    </>
  );
}
